import { Button, Checkbox, FormControlLabel, IconButton, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import classnames from 'classnames';
import { FC, ReactNode } from 'react';
import { ReactComponent as Close } from '../../../../../Asset/svg/close.svg';
import { ReactComponent as UploadSimple } from '../../../../../Asset/svg/UploadSimple10.svg';
import { VideoLanguage } from './bilingual';
import { useVideoImportStyles } from './VideoImport.styles';

const languageLabels: Record<VideoLanguage, string> = { fr: 'Français', en: 'Anglais' };

export type VideoModalCheckboxState = {
  mute: boolean;
  setMute: (value: boolean) => void;
  checkedAuto: boolean;
  setCheckedAuto: (value: boolean) => void;
};

type VideoModalHeaderProps = {
  title: string;
  onClose: () => void;
};

export const VideoModalHeader: FC<VideoModalHeaderProps> = ({ title, onClose }) => {
  const classes = useVideoImportStyles();
  return (
    <div className={classes.videoModalHeader}>
      <Typography id="video-import-dialog" className={classes.videoModalTitle}>
        {title}
      </Typography>
      <IconButton aria-label="Fermer" className={classes.videoModalCloseBtn} onClick={onClose}>
        <Close className={classes.videoModalCloseIcon} />
      </IconButton>
    </div>
  );
};

export const VideoModalCheckboxes: FC<VideoModalCheckboxState> = ({
  mute,
  setMute,
  checkedAuto,
  setCheckedAuto,
}) => {
  const classes = useVideoImportStyles();
  return (
    <div className={classes.videoModalCheckboxes}>
      <FormControlLabel
        className={classes.videoModalCheckboxRow}
        control={
          <Checkbox
            checked={mute}
            onChange={e => setMute(e.target.checked)}
            color="primary"
            className={classes.videoModalCheckbox}
          />
        }
        label={<span className={classes.videoModalCheckboxLabel}>Son actif</span>}
      />
      <FormControlLabel
        className={classes.videoModalCheckboxRow}
        control={
          <Checkbox
            checked={checkedAuto}
            onChange={e => setCheckedAuto(e.target.checked)}
            color="primary"
            className={classes.videoModalCheckbox}
          />
        }
        label={<span className={classes.videoModalCheckboxLabel}>Lecture auto</span>}
      />
    </div>
  );
};

export const VideoLanguageTabs: FC<{
  languages: VideoLanguage[];
  language: VideoLanguage;
  onChange: (language: VideoLanguage) => void;
  missing?: Partial<Record<VideoLanguage, boolean>>;
  label?: string;
}> = ({ languages, language, onChange, missing, label = 'Langue de la vidéo' }) => {
  const classes = useVideoImportStyles();
  if (languages.length < 2) return null;
  return (
    <div className={classes.videoLanguageTabs} role="tablist" aria-label={label}>
      {languages.map(lang => (
        <button
          key={lang}
          type="button"
          role="tab"
          aria-selected={language === lang}
          className={classnames(classes.videoLanguageTab, {
            [classes.videoLanguageTabActive]: language === lang,
          })}
          onClick={() => onChange(lang)}>
          {languageLabels[lang]}
          {missing?.[lang] && (
            <span className={classes.videoLanguageTag}>
              <UploadSimple className={classes.videoLanguageTagIcon} aria-hidden={true} />
              Importer
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

type VideoModalActionsProps = {
  validateLabel?: ReactNode;
  validateDisabled?: boolean;
  onValidate: () => void;
  onBack: () => void;
};

export const VideoModalActions: FC<VideoModalActionsProps> = ({
  validateLabel = 'Valider',
  validateDisabled,
  onValidate,
  onBack,
}) => {
  const classes = useVideoImportStyles();
  return (
    <div className={classes.videoModalActions}>
      <Button
        variant="contained"
        className={classes.videoModalValidateBtn}
        disabled={validateDisabled}
        onClick={onValidate}>
        {validateLabel}
      </Button>
      <button type="button" className={classes.videoModalBackBtn} onClick={onBack}>
        <ArrowBackIcon className={classes.videoModalBackIcon} />
        Retour
      </button>
    </div>
  );
};
